import React, { useState } from 'react';
import {
  FILE_FIELD_LABELS,
  formatFileSize,
  ProposalServiceFile,
} from '../utils/proposalServiceDetails';
import { downloadProposalFile, loadProposalFilePreview } from '../utils/leadFileActions';

type Props = {
  files: ProposalServiceFile[];
  leadId: number | null;
};

const RecuperacaoServiceFiles: React.FC<Props> = ({ files, leadId }) => {
  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    isImage: boolean;
    isPdf: boolean;
  } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  if (!files.length) return null;

  const fileKey = (file: ProposalServiceFile, idx: number) =>
    `${file.field || 'doc'}-${file.lead_file_id || file.name}-${idx}`;

  const handlePreview = async (file: ProposalServiceFile, key: string) => {
    setBusyId(key);
    try {
      const state = await loadProposalFilePreview(file, leadId);
      if (!state) {
        window.alert(`Não foi possível pré-visualizar "${file.name}".`);
        return;
      }
      setPreview({
        url: state.url,
        name: state.name,
        isImage: state.isImage,
        isPdf: state.isPdf,
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (file: ProposalServiceFile, key: string) => {
    setBusyId(key);
    try {
      await downloadProposalFile(file, leadId);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="rec-lead-files">
        <h5 className="rec-lead-files-title">Documentos anexos ({files.length})</h5>
        <ul className="rec-lead-files-list">
          {files.map((file, idx) => {
            const key = fileKey(file, idx);
            const label = file.field ? FILE_FIELD_LABELS[file.field] || file.field : 'Documento';
            const size = formatFileSize(file.size);
            const busy = busyId === key;
            return (
              <li key={key} className="rec-lead-file-item">
                <div className="rec-lead-file-info">
                  <span className="rec-lead-file-label">{label}</span>
                  <span className="rec-lead-file-name">{file.name}</span>
                  {size ? <span className="rec-lead-file-size">{size}</span> : null}
                </div>
                <div className="rec-lead-file-actions">
                  <button
                    type="button"
                    className="rec-lead-file-btn rec-lead-file-btn--muted"
                    disabled={busy}
                    onClick={() => handlePreview(file, key)}
                  >
                    Pré-visualizar
                  </button>
                  <button
                    type="button"
                    className="rec-lead-file-btn rec-lead-file-btn--primary"
                    disabled={busy}
                    onClick={() => handleDownload(file, key)}
                  >
                    Descarregar
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {preview && (
        <div
          className="rec-file-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`Pré-visualização: ${preview.name}`}
          onClick={() => setPreview(null)}
        >
          <div
            className="rec-file-preview-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="rec-file-preview-header">
              <span className="rec-file-preview-title">{preview.name}</span>
              <button
                type="button"
                className="rec-file-preview-close"
                onClick={() => setPreview(null)}
                aria-label="Fechar"
              >
                ×
              </button>
            </header>
            <div className="rec-file-preview-body">
              {preview.isImage ? (
                <img src={preview.url} alt={preview.name} className="rec-file-preview-media" />
              ) : preview.isPdf ? (
                <iframe
                  title={preview.name}
                  src={preview.url}
                  className="rec-file-preview-iframe"
                />
              ) : (
                <iframe
                  title={preview.name}
                  src={preview.url}
                  className="rec-file-preview-iframe"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RecuperacaoServiceFiles;
